import { z } from 'zod'
import { zId, zIdNullable, zIsoDate } from '../common'

// One aggregated traffic row per (propertyId, targetKey): two sites that share
// the same targetKey yield two distinct rows. The array order carries the
// information: the API returns it sorted by `unaffiliatedClicks` descending,
// then `propertySlug`, then `targetKey` — the destinations we send the most
// unpaid traffic to sit at the top. The SPA must NOT re-sort by default; it
// presents this as an opportunity ranking, not a log table.
//
// Diagnostics:
//   hasActiveLink=false + high unaffiliatedClicks → missing program (sign it).
//   hasActiveLink=true  + unaffiliatedClicks>0    → leaking attribution.
//   networkClicks >> clicks                       → leaking tracking (the site
//     under-declares its outbound clicks; a row can even have clicks=0 while a
//     network counted some). `networkClicksByPartner` breaks the total down per
//     network — keys are partner names (see PARTNER_NAMES), possibly empty {}.

// Live AffiliateLink that owns this destination. Null when hasActiveLink is
// false (no programme — a gap). Comes from the link, never from
// networkClicksByPartner: Impact has no click-list API, so it would never
// appear in that breakdown even when it is the live network.
const zLivePartnerFields = {
  affiliatePartnerId: zIdNullable.optional().default(null),
  affiliatePartnerName: z.string().nullable().optional().default(null),
  affiliatePartnerLabel: z.string().nullable().optional().default(null),
}
export const zClickRow = z.object({
  // Aggregation key alongside targetKey. propertyId is normalized to a string
  // like every other id in the app (see zId); propertySlug is the human handle.
  propertyId: zId,
  propertySlug: z.string(),
  targetKey: z.string(),
  clicks: z.number(),
  affiliatedClicks: z.number(),
  unaffiliatedClicks: z.number(),
  hasActiveLink: z.boolean(),
  // Additive fields: default so an older backend (or a fresh targetKey not yet
  // synced) parses cleanly instead of throwing.
  networkClicks: z.number().default(0),
  networkClicksByPartner: z.record(z.number()).default({}),
  // Commission booked on this (site, targetKey), in euros. `pending` is what the
  // network has yet to validate, `validated` what it approved; rejected sales
  // are in neither. Additive/defaulted like the network-click fields above.
  pendingEarnings: z.number().default(0),
  validatedEarnings: z.number().default(0),
  ...zLivePartnerFields,
})
export type ClickRow = z.infer<typeof zClickRow>

// GET /clicks → { clicks: [...] }. No pagination (aggregated, short by nature).
export const zClicksResponse = z.object({
  clicks: z.array(zClickRow),
})
export type ClicksResponse = z.infer<typeof zClicksResponse>

// One row of GET /clicks/gaps — the "which programme to sign next?" list,
// distilled: every (propertyId, targetKey) that took clicks over the window but
// has NO active affiliate link, ranked by unmonetised click volume descending.
// It is the /clicks ranking stripped of everything already monetised, so the
// array order IS the priority order; the SPA must NOT re-sort by default.
export const zClicksGap = z.object({
  propertyId: zId,
  propertySlug: z.string(),
  targetKey: z.string(),
  clicks: z.number(),
})
export type ClicksGap = z.infer<typeof zClicksGap>

// GET /clicks/gaps → { gaps: [...] }. No pagination (aggregated, short).
export const zClicksGapsResponse = z.object({
  gaps: z.array(zClicksGap),
})
export type ClicksGapsResponse = z.infer<typeof zClicksGapsResponse>

// Bucket granularity for GET /clicks/timeseries. Unknown values fall back to
// `day` server-side.
export type ClicksInterval = 'day' | 'week' | 'month'

// One time bucket of the clicks funnel: the same affiliated/unaffiliated split
// as zClickRow, plus the commission booked in that bucket, keyed by the bucket
// start date. The series is ascending by date and only carries buckets with
// activity.
export const zClicksTimeseriesPoint = z.object({
  date: zIsoDate,
  clicks: z.number(),
  affiliatedClicks: z.number(),
  unaffiliatedClicks: z.number(),
  // Additive/defaulted like the earnings fields on zClickRow.
  pendingEarnings: z.number().default(0),
  validatedEarnings: z.number().default(0),
  // The bucket's clicks split per destination, sorted by clicks desc — lets the
  // bar be stacked by destination inside its Monétisés/Non monétisés tones.
  // Additive/defaulted so an older backend parses cleanly.
  byDestination: z
    .array(
      z.object({
        propertyId: zId,
        propertySlug: z.string(),
        targetKey: z.string(),
        clicks: z.number(),
        affiliatedClicks: z.number(),
        unaffiliatedClicks: z.number(),
        ...zLivePartnerFields,
      }),
    )
    .default([]),
})
export type ClicksTimeseriesPoint = z.infer<typeof zClicksTimeseriesPoint>
export type ClicksDestinationSlice = ClicksTimeseriesPoint['byDestination'][number]

// GET /clicks/timeseries → { series: [...] }.
export const zClicksTimeseriesResponse = z.object({
  series: z.array(zClicksTimeseriesPoint),
})
export type ClicksTimeseriesResponse = z.infer<typeof zClicksTimeseriesResponse>
